import { useEffect, useMemo, useState } from "react";

import { api } from "../api";
import type { Lesson } from "../types";
import { haptic, useMainButton } from "../tg";

type Props = {
  // Кеш из App: если уже есть — рендерим мгновенно, без "Загрузка…".
  // Свежий запрос всё равно полетит в useEffect и обновит и локальный state, и App-кеш.
  initialLessons?: Lesson[];
  // Имя раздела из БД. Fallback на старое «Слова-паразиты» если sections ещё не подгрузились.
  sectionTitle: string | undefined;
  onLessonsLoaded: (lessons: Lesson[]) => void;
  pendingSelection: Set<string> | null;
  onPendingSelectionChange: (sel: Set<string>) => void;
  onSaved: (lessons: Lesson[]) => void;
};

export default function ParasitesSelect({
  initialLessons,
  sectionTitle,
  onLessonsLoaded,
  pendingSelection,
  onPendingSelectionChange,
  onSaved,
}: Props) {
  const [lessons, setLessons] = useState<Lesson[] | null>(initialLessons ?? null);
  // Локальное зеркало выбора: либо то что юзер уже отметил в App-state (pendingSelection),
  // либо текущий "сохранённый" набор из lessons (chosen=true). При первом маунте без обоих
  // — null, доинициализируется когда придут lessons.
  const [selected, setSelected] = useState<Set<string> | null>(
    pendingSelection ??
      (initialLessons
        ? new Set(initialLessons.filter((l) => l.chosen).map((l) => l.id))
        : null),
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Подтягиваем свежие уроки. App-кеш обновляем всегда, даже если экран успели размонтировать —
  // иначе кеш в App не успеет заполниться при быстром «вошёл-вышел-вошёл».
  useEffect(() => {
    let cancelled = false;
    api<Lesson[]>("/sections/parasites/lessons")
      .then((data) => {
        onLessonsLoaded(data);
        if (cancelled) return;
        setLessons(data);
      })
      .catch((e: Error) => {
        if (!cancelled && !lessons) setError(e.message);
      });
    return () => {
      cancelled = true;
    };
    // Запрос делаем один раз при маунте — кеш в App переживает unmount при свайпе.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Когда lessons впервые пришли и юзер ещё не успел потыкать — инициализируем selected
  // из chosen. Если уже тыкал (pendingSelection != null) — оставляем его выбор.
  useEffect(() => {
    if (selected !== null) return;
    if (!lessons) return;
    setSelected(new Set(lessons.filter((l) => l.chosen).map((l) => l.id)));
  }, [lessons, selected]);

  const count = selected?.size ?? 0;

  const save = useMemo(
    () => async () => {
      if (saving || !selected) return;
      setSaving(true);
      setError(null);
      try {
        const updated = await api<Lesson[]>("/sections/parasites/selection", {
          method: "POST",
          body: JSON.stringify({ lesson_ids: Array.from(selected) }),
        });
        haptic("success");
        onSaved(updated);
      } catch (e) {
        haptic("error");
        setError((e as Error).message);
        setSaving(false);
      }
    },
    [saving, selected, onSaved],
  );

  useMainButton(
    lessons
      ? {
          text:
            count === 0
              ? "Выберите хотя бы один"
              : `Сохранить (${count})`,
          enabled: count > 0 && !saving,
          loading: saving,
          onClick: save,
        }
      : null,
  );

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev ?? []);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      // Поднимаем "черновик выбора" в App, чтобы при свайпе назад → возврате сюда
      // чекбоксы остались на месте.
      onPendingSelectionChange(next);
      return next;
    });
  };

  return (
    <div className="screen screen-with-sticky-header">
      <header className="lesson-header-sticky">
        <h1>{sectionTitle ?? "Слова-паразиты"}</h1>
        <p className="subtitle">
          Выберите слова-паразиты, с которыми будем работать
        </p>
      </header>

      {error && <div className="error-card">{error}</div>}

      {!lessons && !error && <div className="card">Загрузка…</div>}

      {lessons && selected && (
        <div className="lesson-list">
          {lessons.map((lesson) => {
            const isOn = selected.has(lesson.id);
            return (
              <label
                key={lesson.id}
                className={`lesson-row ${isOn ? "lesson-row-on" : ""}`}
              >
                <input
                  type="checkbox"
                  checked={isOn}
                  onChange={() => toggle(lesson.id)}
                />
                <span className="lesson-row-title">{lesson.title}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
